import { useMemo } from 'react';

import useFacilityDeviceAllocations, {
    InovonicsAllocationTypes,
} from './useFacilityDeviceAllocations';
import sortBy from 'lodash/sortBy';
import { RoomTypeLabels, RoomTypes } from 'src/constants/RoomTypes';
import { TaskUpdateTypes } from 'src/constants/TaskUpdateTypes';
import { DeviceIconType, getDeviceIconType } from 'src/hooks/useDeviceAllocationProperties';
import { AllocationType, Task } from 'src/store/sageAdminApi';

/** One device involved in an alert, and whether it still needs a reset. */
export interface DeviceTriggerState {
    deviceId: string;
    allocationType: AllocationType | undefined;
    /** Where the device is, e.g. "Bathroom". Empty when the allocation has no room. */
    room: string;
    iconType: DeviceIconType;
    isPending: boolean;
}

function usePendingDeviceTriggers(task?: Task) {
    const { deviceIdToAllocationMap } = useFacilityDeviceAllocations();

    const { pendingDeviceIds, knownDeviceIds } = useMemo(() => {
        const orderedUpdates = sortBy(task?.updates ?? [], (update) =>
            parseInt(update.creationTimestampInMs ?? '0', 10)
        );

        const pending = new Set<string>();
        const known = new Set<string>();

        const creatorDeviceId = task?.creator?.device?.id;
        const creatorAllocationType = creatorDeviceId
            ? deviceIdToAllocationMap.get(creatorDeviceId)?.allocationType
            : undefined;
        if (
            creatorDeviceId &&
            creatorAllocationType &&
            InovonicsAllocationTypes.has(creatorAllocationType)
        ) {
            known.add(creatorDeviceId);
            pending.add(creatorDeviceId);
        }

        orderedUpdates.forEach((update) => {
            const deviceId = update.author?.device?.id;
            if (!deviceId) {
                return;
            }
            if (update.type === TaskUpdateTypes.DeviceAlertTriggered) {
                known.add(deviceId);
                pending.add(deviceId);
            } else if (update.type === TaskUpdateTypes.DeviceAlertReset) {
                known.add(deviceId);
                pending.delete(deviceId);
            }
        });

        return { pendingDeviceIds: [...pending], knownDeviceIds: [...known] };
    }, [deviceIdToAllocationMap, task?.creator?.device?.id, task?.updates]);

    const deviceTriggerStates: DeviceTriggerState[] = useMemo(
        () =>
            knownDeviceIds.map((deviceId) => {
                const allocation = deviceIdToAllocationMap.get(deviceId);
                const { room } = allocation ?? {};

                return {
                    deviceId,
                    allocationType: allocation?.allocationType,
                    room: room && room !== RoomTypes.None ? RoomTypeLabels[room] : '',
                    iconType: getDeviceIconType(allocation),
                    isPending: pendingDeviceIds.includes(deviceId),
                };
            }),
        [knownDeviceIds, pendingDeviceIds, deviceIdToAllocationMap]
    );

    return {
        deviceTriggerStates,
        hasPendingDeviceTriggers: pendingDeviceIds.length > 0,
        hasDeviceTriggerLedger: knownDeviceIds.length > 0,
        isReset: knownDeviceIds.length > 0 && pendingDeviceIds.length === 0,
    };
}

export default usePendingDeviceTriggers;
